import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HowItWorks from '../components/HowItWorks';
import { RECRUITER_STEPS } from '../data/onboardingData';
import './RecruiterLanding.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API = `${API_BASE}/api/v1`;

function RecruiterLanding() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);

  useEffect(() => {
    fetch(`${API}/reviews/`)
      .then(r => r.ok ? r.json() : [])
      .then(setReviews)
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > window.innerHeight * 0.8) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    navigate('/recruiter/register');
  };

  const handleLogin = () => {
    navigate('/recruiter/login');
  };

  return (
    <div className="recruiter-landing-page">
      {/* Navigation */}
      <nav className={`recruiter-nav ${isScrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo" onClick={() => navigate('/')}>
            <img src="/JobLinkedAI.png" alt="JobLinked AI" />
            JobLinked AI
          </div>
          <ul className="nav-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#how-it-works">How It Works</a></li>
            <li><button className="btn-nav-secondary" onClick={() => navigate('/')}>For Job Seekers</button></li>
            <li><button className="btn-nav" onClick={handleLogin}>Login</button></li>
          </ul>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="recruiter-hero">
        <div className="stars">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="star"></div>
          ))}
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <h1>Hire Top Talent Faster with AI</h1>
            <p>Post jobs, get qualified candidates, and hire in days—not weeks. Let AI handle screening while you focus on interviewing the best.</p>
            <div className="hero-buttons">
              <button className="btn btn-primary btn-large" onClick={handleGetStarted}>Start Hiring Free</button>
              <button className="btn btn-secondary" onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}>See How It Works</button>
            </div>
            <div style={{ marginTop: '2rem', opacity: 0.9 }}>
              <p style={{ fontSize: '0.9rem' }}>✓ AI-powered screening  ✓ Qualified candidates only  ✓ Hire 50% faster</p>
            </div>
          </div>
          <div className="hero-stats">
            <div className="stat-box">
              <div className="stat-number">50%</div>
              <div className="stat-label">Faster Hiring</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">10x</div>
              <div className="stat-label">Better Matches</div>
            </div>
            <div className="stat-box">
              <div className="stat-number">R5k+</div>
              <div className="stat-label">Saved Per Hire</div>
            </div>
          </div>
        </div>
        <div className="waves">
          <div className="wave"></div>
          <div className="wave"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="recruiter-features" id="features">
        <div className="section-header">
          <h2>Everything You Need to Hire Smarter</h2>
          <p>Powerful tools designed for modern recruiters and hiring managers</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-robot"></i></div>
            <h3>AI Candidate Screening</h3>
            <p>Automatically rank and filter candidates based on skills, experience, and job requirements. Save 10+ hours per position.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-bullseye"></i></div>
            <h3>Smart Job Matching</h3>
            <p>Our AI matches your jobs with the most qualified candidates in our database and notifies them instantly.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-chart-line"></i></div>
            <h3>Hiring Analytics</h3>
            <p>Track time-to-hire, application sources, and candidate quality. Make data-driven hiring decisions.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-star"></i></div>
            <h3>Featured Listings</h3>
            <p>Boost your job visibility with featured placements. Get 3x more qualified applications.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-comments"></i></div>
            <h3>Streamlined Communication</h3>
            <p>Email templates, bulk messaging, and automated notifications keep candidates engaged.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon"><i className="fas fa-building"></i></div>
            <h3>Employer Branding</h3>
            <p>Showcase your company culture with custom pages, photos, and employee testimonials.</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <HowItWorks steps={RECRUITER_STEPS} autoAdvanceMs={4000} />

      {/* Pricing Section */}
      <section className="recruiter-pricing" id="pricing">
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Choose the plan that fits your hiring needs. No hidden fees, cancel anytime.</p>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>Starter</h3>
            <div className="pricing-price">R0</div>
            <div className="pricing-period">Forever Free</div>
            <ul className="pricing-features">
              <li><i className="fas fa-check"></i> 1 active job posting</li>
              <li><i className="fas fa-check"></i> View all applications</li>
              <li><i className="fas fa-check"></i> Basic candidate filtering</li>
              <li><i className="fas fa-check"></i> Email notifications</li>
              <li><i className="fas fa-check"></i> Standard visibility</li>
            </ul>
            <button className="btn" onClick={handleGetStarted}>Get Started</button>
            <p className="pricing-note">Perfect for small businesses</p>
          </div>

          <div className="pricing-card featured">
            <div className="pricing-badge">Most Popular</div>
            <h3>Professional</h3>
            <div className="pricing-price">R499</div>
            <div className="pricing-period">per month</div>
            <ul className="pricing-features">
              <li><i className="fas fa-check"></i> 5 active job postings</li>
              <li><i className="fas fa-check"></i> AI candidate ranking</li>
              <li><i className="fas fa-check"></i> 1 featured listing/month</li>
              <li><i className="fas fa-check"></i> Advanced filters & search</li>
              <li><i className="fas fa-check"></i> Analytics dashboard</li>
              <li><i className="fas fa-check"></i> Email templates</li>
              <li><i className="fas fa-check"></i> Priority support</li>
            </ul>
            <button className="btn btn-primary" onClick={() => navigate('/recruiter/register')}>Get Started</button>
            <p className="pricing-note">14-day free trial, no credit card</p>
          </div>

          <div className="pricing-card">
            <h3>Business</h3>
            <div className="pricing-price">R999</div>
            <div className="pricing-period">per month</div>
            <ul className="pricing-features">
              <li><i className="fas fa-check"></i> 15 active job postings</li>
              <li><i className="fas fa-check"></i> AI candidate recommendations</li>
              <li><i className="fas fa-check"></i> Unlimited featured listings</li>
              <li><i className="fas fa-check"></i> Full analytics suite</li>
              <li><i className="fas fa-check"></i> Automated screening</li>
              <li><i className="fas fa-check"></i> Custom company branding</li>
              <li><i className="fas fa-check"></i> Bulk actions</li>
              <li><i className="fas fa-check"></i> Priority chat support</li>
            </ul>
            <button className="btn" onClick={() => navigate('/recruiter/register')}>Get Started</button>
            <p className="pricing-note">For growing companies</p>
          </div>
        </div>

        <div className="enterprise-cta">
          <h3>Need more? Enterprise plans available</h3>
          <p>Unlimited postings, dedicated support, API access, and custom integrations</p>
          <a href="mailto:enterprise@joblinked.ai" className="enterprise-sales-btn">
            <i className="fas fa-envelope"></i> Contact Sales
          </a>
        </div>
      </section>

      {/* ROI Section */}
      <section className="roi-section">
        <div className="roi-container">
          <h2>The Numbers Speak for Themselves</h2>
          <div className="roi-grid">
            <div className="roi-card">
              <div className="roi-label">Traditional Hiring Cost</div>
              <div className="roi-value">R15,000 - R30,000</div>
              <div className="roi-detail">Per hire</div>
            </div>
            <div className="roi-arrow">→</div>
            <div className="roi-card highlight">
              <div className="roi-label">With JobLinked AI</div>
              <div className="roi-value">R999/month</div>
              <div className="roi-detail">Unlimited hires</div>
            </div>
          </div>
          <p className="roi-conclusion">Save R5,000 - R15,000 per hire. Break even with just 1 successful hire!</p>
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section style={{ padding: '100px 5%', background: 'white' }}>
          <div className="section-header">
            <h2>What People Say About JobLinked AI</h2>
            <p>Real feedback from recruiters and job seekers across South Africa</p>
          </div>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {(showAllReviews ? reviews : reviews.slice(0, 6)).map(r => (
              <div key={r.id} style={{ background: '#f8f9fa', borderRadius: '16px', padding: '1.75rem', display: 'flex', flexDirection: 'column', gap: '0.85rem', boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingBottom: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                    {r.avatar_url
                      ? <img src={`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${r.avatar_url.startsWith('/') ? '' : '/'}${r.avatar_url}`}
                          alt={r.full_name} style={{ width: '100%', height: '100%', objectFit: 'contain', background: 'white', padding: '3px' }} />
                      : r.full_name.charAt(0).toUpperCase()
                    }
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>{r.full_name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{r.company_or_title || (r.user_type === 'RECRUITER' ? 'Recruiter' : 'Job Seeker')}</div>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {new Date(r.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.2rem' }}>
                  {[1,2,3,4,5].map(s => <i key={s} className="fas fa-star" style={{ color: s <= r.rating ? '#f59e0b' : '#e2e8f0', fontSize: '0.9rem' }}></i>)}
                </div>
                <p style={{ color: '#4b5563', fontSize: '0.95rem', lineHeight: 1.7, fontStyle: 'italic', margin: 0 }}>"{r.review_text}"</p>
              </div>
            ))}
          </div>
          {reviews.length > 6 && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button onClick={() => setShowAllReviews(v => !v)}
                style={{ background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white', border: 'none', padding: '0.75rem 2.5rem', borderRadius: '50px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer' }}>
                {showAllReviews ? 'Show Less' : `Show More (${reviews.length - 6} more)`}
              </button>
            </div>
          )}
        </section>
      )}

      {/* CTA Section */}
      <section className="cta">
        <div className="stars">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="star"></div>
          ))}
        </div>
        <h2>Ready to Transform Your Hiring?</h2>
        <p>Join hundreds of South African companies hiring smarter with AI</p>
        <button className="btn btn-primary btn-large" onClick={handleGetStarted}>Start Hiring Free</button>
        <p style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.8 }}>No credit card required • 14-day free trial • Cancel anytime</p>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <p>&copy; 2025 JobLinked AI. All rights reserved.</p>
        <p style={{ marginTop: '1rem' }}>Built with ❤️ in South Africa</p>
      </footer>
    </div>
  );
}

export default RecruiterLanding;
